import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { isMissingRoute } from './ehrMapThisApi';
import type { EhrAddedItemCount, EhrAddedItemList, EhrAddedItemReviewResult } from '../types/ehrAddedItems';

// React Query hooks for EHR > Added Items (EhrAttachmentReviewController).

export const addedItemsKeys = {
  all: ['ehrAddedItems'] as const,
  list: (facilityId: number, reviewed: boolean) => [...addedItemsKeys.all, 'list', facilityId, reviewed] as const,
  count: (facilityId: number) => [...addedItemsKeys.all, 'count', facilityId] as const,
};

/** How often the tab's badge asks again. */
export const ADDED_ITEMS_COUNT_REFRESH_MS = 2 * 60 * 1000;

export function useAddedItems(facilityId: number, reviewed: boolean, enabled = true) {
  return useQuery({
    queryKey: addedItemsKeys.list(facilityId, reviewed),
    queryFn: async () =>
      (await apiClient.get<EhrAddedItemList>(API.ehrAddedItemsList(facilityId, reviewed), { _skipVersionMismatch: true } as never)).data,
    enabled: enabled && facilityId > 0,
    retry: (count, error) => !isMissingRoute(error) && count < 2,
  });
}

/** The items waiting for review: the Added Items tab's badge. Quiet on a 404 (an older API slot). */
export function useAddedItemsCount(facilityId: number, enabled = true, queryClient?: QueryClient) {
  return useQuery({
    queryKey: addedItemsKeys.count(facilityId),
    queryFn: async () =>
      (await apiClient.get<EhrAddedItemCount>(API.ehrAddedItemsCount(facilityId), { _skipVersionMismatch: true } as never)).data,
    enabled: enabled && facilityId > 0,
    refetchInterval: ADDED_ITEMS_COUNT_REFRESH_MS,
    staleTime: 60 * 1000,
    retry: (count, error) => !isMissingRoute(error) && count < 1,
  }, queryClient);
}

export function useMarkAddedItemsReviewed(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) =>
      (await apiClient.post<EhrAddedItemReviewResult>(API.ehrAddedItemsMark(facilityId), { ids })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: addedItemsKeys.all }),
  });
}
